import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Sparkles, RefreshCw } from 'lucide-react';
import { getNarrative, qk, refreshNarrative } from '../api.js';
import { parseBold } from '../lib/markdown-bold.js';

/**
 * §25.7 — "Atlas özeti" kartı.
 *
 * Kart YALNIZ anlatı varsa render edilir: uç 404 dönerse (hiç üretilmemiş ya da
 * `LLM_PROVIDER=off`) hiçbir şey çizilmez — boş bir "özet yok" kutusu, kullanıcıya
 * özellik bozukmuş izlenimi verirdi.
 */
export default function AtlasCard({ pid }: { pid: string }) {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: qk.narrative(pid),
    queryFn: ({ signal }) => getNarrative(pid, signal),
    // 404 normal bir durumdur; yeniden denemeye gerek yok.
    retry: false,
  });

  const yenile = useMutation({
    mutationFn: () => refreshNarrative(pid),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: qk.narrative(pid) });
    },
  });

  if (query.isLoading || query.isError || !query.data) return null;
  const n = query.data;

  /*
   * §25.5 — tavan durumu ÖNCE yanıttan okunur. Yalnız yenileme hatasına bakmak,
   * rozeti kullanıcı düğmeye BASANA KADAR gizlerdi; oysa §25.5 tavan dolduğunda
   * görünmesini ister. Yenileme hatası ikinci kaynaktır (yanıt bayat kalmışsa).
   */
  const yenilemeHatasi = String((yenile.error as Error | null)?.message ?? '');
  const tavanDolu = n.caps?.monthlyFull === true || /monthly_cap/.test(yenilemeHatasi);
  const gunTavani = n.caps?.dailyFull === true || /daily_cap/.test(yenilemeHatasi);

  const zaman = new Date(n.createdAt);
  const zamanMetni = Number.isNaN(zaman.getTime())
    ? n.createdAt
    : zaman.toLocaleString('tr-TR', { dateStyle: 'medium', timeStyle: 'short' });

  return (
    <div
      data-testid="atlas-karti"
      className="mb-6 rounded-2xl border border-[#e5e7eb] bg-white p-5 shadow-sm"
    >
      <div className="mb-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-brand" aria-hidden="true" />
          <h2 className="font-grotesk text-[15px] font-bold text-[#111827]">Atlas özeti</h2>
          {tavanDolu ? (
            <span
              data-testid="atlas-tavan-rozeti"
              className="rounded-md bg-[#fef3c7] px-2 py-0.5 text-[11px] font-medium text-[#92400e]"
            >
              aylık anlatı bütçesi doldu
            </span>
          ) : null}
          {gunTavani ? (
            <span className="rounded-md bg-[#f3f4f6] px-2 py-0.5 text-[11px] text-[#6b7280]">
              bugünlük yenileme hakkı doldu
            </span>
          ) : null}
        </div>
        <button
          type="button"
          data-testid="atlas-yenile"
          disabled={yenile.isPending}
          onClick={() => yenile.mutate()}
          className="flex items-center gap-1.5 rounded-lg px-2 py-1 text-[12px] text-[#6b7280] transition-colors hover:bg-[#f3f4f6] hover:text-[#111827] disabled:opacity-40"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${yenile.isPending ? 'animate-spin' : ''}`} />
          Yenile
        </button>
      </div>

      <div data-testid="atlas-icerik" className="space-y-3 text-[14px] leading-relaxed text-[#374151]">
        {n.content.split('\n\n').map((paragraf, i) => (
          <p key={i}>
            {parseBold(paragraf).map((s, j) =>
              s.bold ? (
                <strong key={j} className="font-semibold text-[#111827]">
                  {s.text}
                </strong>
              ) : (
                <span key={j}>{s.text}</span>
              ),
            )}
          </p>
        ))}
      </div>

      <div data-testid="atlas-meta" className="mt-3 text-[11px] text-[#9ca3af]">
        {n.model} · ${n.costUsd.toFixed(4)} · {zamanMetni}
      </div>
    </div>
  );
}
